<div
  class="chat-input-container"
  data-testid="chat-input"
  [attr.data-context-project-id]="context?.projectId || ''"
  [attr.data-context-file-id]="context?.fileId || ''"
  [attr.data-context-page-number]="context?.pageNumber || ''"
  [class.drag-over]="isDragOver"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)"
>
  <!-- Announces attachment changes to assistive technology. Pasting an image has no visual moment
       of its own, so without this a screen-reader user would not know an attachment appeared. -->
  <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
    {{ attachmentAnnouncement }}
  </div>

  @if (isDragOver) {
    <div class="drop-overlay" aria-hidden="true">
      <mat-icon>add_photo_alternate</mat-icon>
      <span>Drop images to attach</span>
      <span class="drop-overlay-limits">
        PNG or JPEG, up to {{ attachmentLimits.maxBytesPerFile / 1024 / 1024 }} MB, at most
        {{ attachmentLimits.maxAttachmentsPerMessage }} per message
      </span>
    </div>
  }

  <div
    class="input-wrapper"
    [class.has-context]="hasContext()"
    [class.focused]="isFocused && !isVoiceActive"
    [class.voice-mode]="isVoiceActive"
  >
    <!-- Attached images for the message being composed -->
    @if (attachments.length > 0) {
      <app-attachment-thumbnails
        [attachments]="attachments"
        [removable]="true"
        (removed)="removeAttachment($event)"
      ></app-attachment-thumbnails>
    }

    <div class="input-row">
      <!-- Context toggle button on the left. Always shown, because the panel it opens also holds
           the attach-image button, which must be reachable even when there is no context yet. -->
      <button
        mat-mini-fab
        type="button"
        (click)="toggleContext()"
        [matTooltip]="contextExpanded ? 'Hide context management' : 'Manage context'"
        class="context-toggle-btn"
        [class.expanded]="contextExpanded"
        aria-label="Toggle context panel"
        aria-controls="context-zippy"
        [attr.aria-expanded]="contextExpanded"
      >
        <mat-icon>add</mat-icon>
      </button>

      @if (isVoiceActive) {
        <!-- ═══ VOICE MODE: replaces only the textarea ═══ -->
        <div class="voice-area" [style.--audio-level]="audioLevel">
          <div class="vu-meter-large" aria-label="Audio level">
            @for (bar of vuBars; track $index) {
              <div class="vu-bar" [class.active]="audioLevel > bar.threshold" [class.muted-bar]="isMuted"></div>
            }
          </div>
          <span
            class="voice-status"
            [class.muted-status]="isMuted"
            [class.connecting-status]="voiceState === 'connecting' || voiceState === 'reconnecting'"
          >
            {{ voiceStatusText }}
          </span>
          <span class="session-timer" matTooltip="Voice session duration">
            {{ sessionDurationFormatted }}
          </span>
          @if (inactivityCountdown !== null) {
            <span
              class="inactivity-timer"
              [class.inactivity-urgent]="inactivityCountdown < 60"
              matTooltip="Session disconnects after inactivity"
            >
              <mat-icon>hourglass_bottom</mat-icon>
              {{ inactivityFormatted }}
            </span>
          }
        </div>
      } @else {
        <!-- ═══ TEXT MODE: normal textarea ═══ -->
        <mat-form-field appearance="fill" class="message-field">
          <textarea
            matInput
            [(ngModel)]="message"
            [disabled]="disabled"
            (keydown)="onKeydown($event)"
            (paste)="onPaste($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            [attr.aria-describedby]="'attachment-hint'"
            placeholder="Ask a question..."
            data-testid="chat-prompt"
            rows="1"
            cdkTextareaAutosize
            cdkAutosizeMinRows="1"
            cdkAutosizeMaxRows="5"
          ></textarea>
        </mat-form-field>
      }

      <!-- Action buttons inside the input row (right-aligned) -->
      <div class="input-actions">
        @if (voiceModeEnabled) {
          <!-- Mic mute (visible in voice mode) — to the left of call/hangup -->
          <button
            mat-icon-button
            (click)="toggleMute()"
            [class.muted]="isMuted"
            [class.recording]="!isMuted"
            [style.--audio-level]="audioLevel"
            [matTooltip]="isMuted ? 'Unmute microphone' : 'Mute microphone'"
            aria-label="Toggle microphone mute"
            class="mic-mute-button"
          >
            <mat-icon>{{ isMuted ? 'mic_off' : 'mic' }}</mat-icon>
          </button>
        }

        @if (isLoading) {
          <!-- Stop generation (replaces send while streaming) -->
          <button
            mat-icon-button
            class="stop-button"
            (click)="stopRequested.emit()"
            matTooltip="Stop generation"
            aria-label="Stop generation"
          >
            <mat-icon>stop_circle</mat-icon>
          </button>
        } @else if ((message.trim() || attachments.length > 0) && !isVoiceActive) {
          <!-- Send button. Shown once there is text or at least one image; an image-only message
               is allowed, and the button stays disabled while an upload is still settling so a
               half-uploaded picture cannot be sent. -->
          <button
            mat-icon-button
            color="primary"
            [disabled]="disabled || !canSend"
            (click)="send()"
            [matTooltip]="hasUnsettledAttachments ? 'Waiting for images to finish uploading' : 'Send message (Enter)'"
            aria-label="Send message"
          >
            <mat-icon>send</mat-icon>
          </button>
        } @else {
          <!-- Voice call toggle (shown when input is empty / during voice mode) -->
          <button
            mat-icon-button
            (click)="toggleVoiceMode()"
            [class.active]="voiceModeEnabled"
            [class.voice-connecting]="isVoiceConnecting"
            [class.voice-error]="voiceState === 'error'"
            [class.voice-stop-btn]="voiceModeEnabled && voiceState !== 'connecting'"
            [class.voice-start-btn]="!voiceModeEnabled && !isVoiceConnecting"
            [disabled]="voiceState === 'connecting' || !isVoiceAvailable"
            [matTooltip]="voiceTooltipText"
            aria-label="Toggle live voice chat"
          >
            @if (isVoiceConnecting) {
              <mat-icon>hourglass_top</mat-icon>
            } @else if (voiceModeEnabled) {
              <mat-icon class="symbol-icon" fontSet="material-symbols-outlined">call_end</mat-icon>
            } @else {
              <mat-icon class="symbol-icon" fontSet="material-symbols-outlined">call</mat-icon>
            }
          </button>
        }
      </div>
    </div>

    <!-- Animated context zippy (collapsed by default). While collapsed it is inert, so its buttons
         cannot be reached with Tab while they are invisible. -->
    <div
      id="context-zippy"
      class="context-zippy"
      [class.context-zippy-open]="contextExpanded"
      [attr.inert]="contextExpanded ? null : ''"
    >
      <div class="context-chips-container">
        @if (hasContext()) {
          <span class="context-label">Context:</span>
          @if (context?.projectId) {
            <mat-chip [removable]="true" (removed)="removeContextItem('project')">
              <mat-icon class="chip-icon">folder</mat-icon>
              {{ context.projectName || context.projectId }}
              <button matChipRemove aria-label="Remove project context">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
          @if (context?.fileId) {
            <mat-chip [removable]="true" (removed)="removeContextItem('file')">
              <mat-icon class="chip-icon">description</mat-icon>
              {{ context.fileName || context.fileId }}
              <button matChipRemove aria-label="Remove file context">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
          @if (context?.pageNumber) {
            <mat-chip [removable]="true" (removed)="removeContextItem('page')">
              <mat-icon class="chip-icon">article</mat-icon>
              Page {{ context.pageNumber }}
              <button matChipRemove aria-label="Remove page context">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
          @for (pin of context?.pinnedPages || []; track pin) {
            <mat-chip [removable]="true" (removed)="removePinnedPage(pin)">
              <mat-icon class="chip-icon">push_pin</mat-icon>
              {{ pin.pageTitle || 'Page ' + pin.pageNumber }}
              <button matChipRemove [attr.aria-label]="'Unpin page ' + pin.pageNumber">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
          @for (quote of context?.selectedTexts || []; track quote.id || quote.selectedText) {
            <mat-chip
              [removable]="true"
              (removed)="removeSelectedText(quote)"
              class="quote-context-chip"
              [matTooltip]="quote.selectedText"
            >
              <mat-icon class="chip-icon">format_quote</mat-icon>
              "{{ quote.excerpt || quote.selectedText }}" (P. {{ quote.pageNumber }})
              <button matChipRemove aria-label="Remove quote context">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
        }
        <!-- The add menu sits after the chips and is shown with or without context. "Add context"
             is not offered yet: its handler is still a stub (Issue #2914). -->
        <button
          mat-icon-button
          type="button"
          class="add-context-btn-inline"
          [matMenuTriggerFor]="addMenu"
          matTooltip="Add to this message"
          aria-label="Add to this message"
        >
          <mat-icon>add_circle</mat-icon>
        </button>
        <mat-menu #addMenu="matMenu" class="chat-add-menu">
          <!-- Attach image. Drag-and-drop and clipboard paste both work, but neither is the only
               way in: this item keeps the feature reachable by keyboard and by pointer. -->
          <button
            mat-menu-item
            type="button"
            class="attach-button"
            [disabled]="!canAttach"
            (click)="openFilePicker()"
            aria-describedby="attachment-hint"
          >
            <mat-icon>add_photo_alternate</mat-icon>
            <span>Attach image</span>
          </button>
          @if (!canAttach) {
            <!-- A disabled item shows no tooltip, so the reason is written out under it. -->
            <div class="attach-unavailable-reason">{{ attachTooltip }}</div>
          }
        </mat-menu>
      </div>
    </div>
  </div>

  <!-- The picker's input lives outside the zippy so its ViewChild exists however the panel is set. -->
  <input
    #fileInput
    type="file"
    class="visually-hidden"
    [accept]="attachmentLimits.acceptAttribute"
    multiple
    tabindex="-1"
    aria-hidden="true"
    (change)="onFilesChosen($event)"
  />

  <!-- Status bar (toggled via 3-dot menu) -->
  @if (showStatusBar) {
    <div class="status-bar">
      <div class="status-left">
        <mat-select
          class="model-selector"
          data-testid="chat-model-selector"
          [value]="displayedModelId"
          (selectionChange)="onModelSelectionChanged($event.value)"
          [disabled]="voiceState === 'connecting'"
          panelClass="model-selector-panel"
          panelWidth="auto"
          aria-label="AI model"
          matTooltip="Select AI model"
        >
          @for (model of visibleModels; track model.id) {
            <mat-option [value]="model.id">{{ model.label }}</mat-option>
          }
        </mat-select>

        <mat-select
          class="thinking-selector"
          [value]="selectedThinkingLevel"
          (selectionChange)="thinkingLevelChanged.emit($event.value)"
          panelClass="model-selector-panel"
          panelWidth="auto"
          aria-label="Thinking level"
          [matTooltip]="currentThinkingMode === 'BUDGET' ? 'Thinking token budget' : 'Thinking level'"
        >
          @for (level of thinkingLevels; track level.id) {
            <mat-option [value]="level.id">{{ level.label }}</mat-option>
          }
        </mat-select>

        @if (isCustomBudget) {
          <input
            type="number"
            class="custom-budget-input"
            [value]="customBudgetInput"
            (change)="onCustomBudgetChange($any($event.target).value)"
            min="0"
            max="32768"
            step="512"
            placeholder="Budget"
            matTooltip="Custom thinking token budget"
          />
        }

        <div class="status-spacer"></div>

        <span
          class="token-counter token-counter--read"
          matTooltip="Tokens read (cumulative billing total across all turns — each turn re-reads conversation history)"
        >
          <mat-icon>visibility</mat-icon>
          {{ tokensRead.toLocaleString() }}
        </span>
        <span
          class="token-counter token-counter--written"
          matTooltip="Tokens written (cumulative total of all model responses)"
        >
          <mat-icon>edit</mat-icon>
          {{ tokensWritten.toLocaleString() }}
        </span>
        <span class="context-fill-meter {{ contextFillColor }}" [matTooltip]="contextFillTooltip">
          <mat-icon>memory</mat-icon>
          {{ contextFill?.fillPercent ?? 0 }}%
        </span>
        <span class="cost-display" matTooltip="Total cost of conversation">
          <mat-icon>attach_money</mat-icon>
          {{ totalCost.toFixed(4) }}
        </span>
      </div>
    </div>
  }
  <!-- Stating the accepted formats before someone tries an unsupported one is kinder than
       refusing them afterwards, and a clipboard paste bypasses the picker's own filter. The line is
       only visible while images are being attached; the rest of the time it stays screen-reader-only
       text, so the attach button's aria-describedby still reads the formats and limits aloud. -->
  <div id="attachment-hint" class="attachment-hint" [class.visually-hidden]="!showAttachmentHint">
    Attach PNG or JPEG images up to {{ attachmentLimits.maxBytesPerFile / 1024 / 1024 }} MB, at most
    {{ attachmentLimits.maxAttachmentsPerMessage }} per message. Drag files in or paste a screenshot.
  </div>
  <div class="ai-disclaimer">PermitProof is powered by AI and can make mistakes.</div>
</div>
